/**
 * Dismissible in-app banner asking established practices to share a quote.
 *
 * Shown when: loginCount >= 3 AND user hasn't already dismissed the prompt
 * AND practice is in trial/trial_registered/active status.
 *
 * Used by PracticeDashboard. Self-contained: reads current user from AuthContext
 * via prop, persists dismissal via /api/practice/dismiss-testimonial-prompt.
 */
import React, { useState } from 'react';
import { Quote, X, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialPromptBanner({ user, token }) {
  const [dismissed, setDismissed] = useState(
    !!user?.testimonialPromptDismissedAt || !!localStorage.getItem('testimonialPromptDismissed')
  );

  if (dismissed) return null;
  if (!user) return null;

  // Show only after a few logins so the user has had time to form an opinion
  const loginCount = user?.loginCount ?? user?.practice?.loginCount ?? 0;
  if (loginCount < 3) return null;

  const handleDismiss = async () => {
    setDismissed(true);
    localStorage.setItem('testimonialPromptDismissed', '1');
    try {
      await fetch(`${API}/api/practice/dismiss-testimonial-prompt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  };

  const buildShareUrl = () => {
    const params = new URLSearchParams();
    const nm = user?.firstName || user?.practice?.ownerName || '';
    if (nm) params.set('name', nm.split(' ')[0]);
    if (user?.practice?.name) params.set('practice', user.practice.name);
    if (user?.email) params.set('email', user.email);
    if (user?.practiceId) params.set('practiceId', user.practiceId);
    return `/founder/share?${params.toString()}`;
  };

  return (
    <div
      data-testid="testimonial-prompt-banner"
      className="relative bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 border-2 border-indigo-200 rounded-xl p-4 sm:p-5 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-sm"
    >
      <div className="bg-indigo-600 text-white rounded-lg p-2.5 shrink-0">
        <Quote className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-indigo-900 text-sm sm:text-base">
          Got 30 seconds? Tell Cary what you'd say to another dentist about PractCom.
        </p>
        <p className="text-xs sm:text-sm text-indigo-700 mt-0.5">
          Your one-sentence story could help another practice owner stop doing paperwork at 5:30 PM.
        </p>
      </div>
      <a
        href={buildShareUrl()}
        data-testid="testimonial-prompt-cta"
        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-1 shrink-0"
      >
        Share my story
        <ChevronRight className="w-4 h-4" />
      </a>
      <button
        data-testid="testimonial-prompt-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 sm:relative sm:top-0 sm:right-0 p-1.5 text-indigo-400 hover:text-indigo-700 hover:bg-indigo-100 rounded-md"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
